import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useDeviceLimit } from "@/hooks/useDeviceLimit";
import { useUserData } from "@/hooks/useUserData";
import { usePreferences } from "@/hooks/usePreferences";
import { useTranslation } from "react-i18next";

/**
 * Invisible component that handles device registration and
 * one-time migration of localStorage data to the database on first login.
 * Also hydrates localStorage from DB prefs so Discover/GroupWaitingRoom
 * can read them synchronously.
 */
const AppInitializer = () => {
  useDeviceLimit();
  const { user } = useAuth();
  const { migrateLocalToDb } = useUserData();
  const { migratePrefsToDb, getRegion, getProviders, getOnboarded } = usePreferences();
  const { i18n } = useTranslation();
  const migratedRef = useRef(false);

  useEffect(() => {
    if (user && !migratedRef.current) {
      migratedRef.current = true;
      const alreadyMigrated = localStorage.getItem("ayeris_db_migrated");
      if (!alreadyMigrated) {
        migrateLocalToDb().then(() => {
          localStorage.setItem("ayeris_db_migrated", "true");
        });
      }

      // Migrate review pending from localStorage to DB
      const reviewMigrated = localStorage.getItem("ayeris_review_migrated");
      if (!reviewMigrated) {
        const rawReview = localStorage.getItem("ayeris_review_pending");
        if (rawReview) {
          const items = JSON.parse(rawReview);
          Promise.all(
            items.map((movie: any) =>
              (supabase.from("user_review_pending") as any).upsert({
                user_id: user.id,
                tmdb_id: movie.id,
                media_type: movie.media_type || "movie",
                title: movie.title,
                poster: movie.poster || "",
                year: movie.year || 0,
                runtime: movie.runtime || 0,
                rating: movie.rating || 0,
                genres: movie.genres || [],
                providers: movie.providers || [],
              }, { onConflict: "user_id,tmdb_id" })
            )
          ).then(() => {
            localStorage.setItem("ayeris_review_migrated", "true");
          });
        } else {
          localStorage.setItem("ayeris_review_migrated", "true");
        }
      }
      // Prefs migration uses a separate flag so existing users get their
      // region/providers/onboarded synced even if data migration already ran
      const prefsMigrated = localStorage.getItem("ayeris_prefs_migrated");
      if (!prefsMigrated) {
        migratePrefsToDb().then(() => {
          localStorage.setItem("ayeris_prefs_migrated", "true");
        });
      }

      // Migrate age gate data from localStorage to DB profile
      // (age gate runs before signup, so data is only in localStorage)
      const birthMonth = localStorage.getItem("ayeris_birth_month");
      if (birthMonth) {
        const ageData: Record<string, unknown> = {
          birth_month: parseInt(birthMonth),
        };
        const is15 = localStorage.getItem("ayeris_is_15_plus");
        const is18 = localStorage.getItem("ayeris_is_18_plus");
        const lvy = localStorage.getItem("ayeris_last_verified_year");
        if (is15 !== null) ageData.is_15_plus = is15 === "true";
        if (is18 !== null) ageData.is_18_plus = is18 === "true";
        if (lvy) ageData.last_verified_year = parseInt(lvy);

        (supabase.from("profiles") as any)
          .update(ageData)
          .eq("id", user.id)
          .then(() => {});
      }

      // Save current language to profile
      const currentLang = i18n.language?.split("-")[0] || "en";
      (supabase.from("profiles") as any)
        .update({ language: currentLang })
        .eq("id", user.id)
        .then(() => {});

      // Always hydrate localStorage from DB so sync reads work
      getRegion();
      getProviders();
      getOnboarded();

      // Hydrate language from DB
      (supabase.from("profiles") as any)
        .select("language")
        .eq("id", user.id)
        .single()
        .then(({ data }: any) => {
          if (data?.language && data.language !== currentLang) {
            i18n.changeLanguage(data.language);
          }
        });
    }
    if (!user) {
      migratedRef.current = false;
    }
  }, [user, migrateLocalToDb, migratePrefsToDb, getRegion, getProviders, getOnboarded, i18n]);

  return null;
};

export default AppInitializer;
